// MACHINERY V2 (Stroje) — "Pridať zariadenie", step 0 of the add flow (M2.7).
//
// Spec: data package §10, the three `[HUB]` rows. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx `AddMachinePicker` (lines
// 167-200). Artboards 0800 (over the fleet) / 0801 (isolated).
//
// >>> THIS COMPONENT OWNS THE WHOLE ADD FLOW, NOT JUST THE PICKER. <<<
// Artboards 0802-0804 give "Vlastný stroj" its own dialog with a "Späť na
// možnosti" link back here, so the two are one flow with one open/close pair.
// The fleet page mounts THIS and nothing else; `stage` decides which dialog is
// on screen. Keeping the pair in one owner is the same reasoning
// fleet/useFleetDialogs.ts records for its six dialogs — split ownership is how
// a dialog ends up stuck open.
//
// >>> THE THREE PATHS, AND WHERE EACH ONE GOES TODAY. <<<
//  1. SEARCH a catalog model -> `onCatalogModel(model)`. §10: "Selecting
//     SWITCHES to catalog mode", i.e. the reduced serial + year + location
//     form. M2.8 built that form (`catalog/CatalogAddDialogV2`) and
//     fleet/FleetDialogs.tsx now mounts IT instead of v1's MachineDialog — the
//     `preSelectedCatalogModel` hand-off is gone with the MUI import.
//     THIS PICKER CLOSES BEFORE THE FORM OPENS, and that is deliberate: two
//     stacked dialogs is not the artboard. The way back is not lost — the form
//     draws "Späť na možnosti" / "Zmeniť stroj" (artboard 0903) and the fleet
//     mount wires them to re-open this picker (fleet/useFleetDialogs.ts
//     `onAddBack`). Until M2.8 that wiring did not exist and both controls
//     rendered as empty space, which is what made this ordering a dead end.
//  2. BROWSE the catalog -> the v2 catalog, `/machinery-v2/catalog` (M2.8
//     mounted the route and this line was repointed off the live
//     `/machinery/catalog` at the same time).
//  3. CUSTOM machine -> stage "custom", CustomMachineDialog.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { CatalogModelListItem } from "../../models/models";
import { CATALOG_V2_PATH } from "../catalog/catalogChrome";
import { ListGlyph, WrenchGlyph } from "../forms/FormGlyphs";
import TabDialog from "../profile/tabs/TabDialog";
import { OrDivider, PathCard, RobotGlyph } from "./addAtoms";
import CatalogSearchField from "./CatalogSearchField";
import CustomMachineDialog from "./CustomMachineDialog";

/** The V2 catalog route (M2.8). Single source: catalog/catalogChrome.ts, which
 *  builds it off `MACHINERY_V2_BASE` — so the cutover at M2.12 moves one
 *  constant, not two. */
const CATALOG_PATH = CATALOG_V2_PATH;

export interface AddMachinePickerProps {
  open: boolean;
  onClose: () => void;
  /** Fired only after createMachine resolves — id of the new machine. */
  onCreated: (id?: string) => void;
  /** Hand-off to the catalog add form (see path 1 in the header). */
  onCatalogModel: (model: CatalogModelListItem) => void;
}

const AddMachinePicker: React.FC<AddMachinePickerProps> = ({
  open,
  onClose,
  onCreated,
  onCatalogModel,
}) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const navigate = useNavigate();
  const [stage, setStage] = useState<"picker" | "custom">("picker");

  const close = () => {
    setStage("picker");
    onClose();
  };

  if (stage === "custom") {
    return (
      <CustomMachineDialog
        open={open}
        onClose={close}
        onBack={() => setStage("picker")}
        onCreated={(id) => {
          setStage("picker");
          onCreated(id);
        }}
      />
    );
  }

  return (
    <TabDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
      width={620}
      title={translate("myMachinery:addMachine")}
      glyph={<RobotGlyph />}
      footerNote={translate("myMachinery:v2.add.dismissNote")}
      footer={
        <button type="button" className="btn btn-ghost" onClick={close}>
          {translate("common:buttons.cancel")}
        </button>
      }
    >
      <CatalogSearchField
        onSelect={(model) => {
          // Close first, then hand off — never two dialogs at once. Reversible:
          // see path 1 in the header.
          close();
          onCatalogModel(model);
        }}
      />

      <OrDivider label={translate("myMachinery:v2.add.or")} />

      <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
        <PathCard
          primary
          glyph={<ListGlyph />}
          title={translate("myMachinery:browseCatalog")}
          hint={translate("myMachinery:v2.add.browseCatalogHint")}
          onClick={() => {
            close();
            navigate(CATALOG_PATH);
          }}
        />
        <PathCard
          glyph={<WrenchGlyph />}
          title={translate("myMachinery:v2.add.customTitle")}
          hint={translate("myMachinery:v2.add.customHint")}
          onClick={() => setStage("custom")}
        />
      </div>
    </TabDialog>
  );
};

export default AddMachinePicker;
